<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'configure',
    subject: 'Workflow',
  },
})

const ability = useAbility()

const items = ref([])
const loading = ref(false)

const dialog = ref(false)
const saving = ref(false)
const errors = ref({})

const emptyStep = () => ({ order: 1, name: '', approver_role: '' })
const formDefault = () => ({ name: '', code: '', approvable_type: '', is_active: true, steps: [emptyStep()] })
const form = ref(formDefault())

const fetchItems = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/v1/approval-flows').get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : []
  } catch (e) {
    console.error('Fetch approval flows error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchItems)

const openCreate = () => {
  form.value = formDefault()
  errors.value = {}
  dialog.value = true
}

const addStep = () => {
  form.value.steps.push({ order: form.value.steps.length + 1, name: '', approver_role: '' })
}

const removeStep = index => {
  form.value.steps.splice(index, 1)
  form.value.steps.forEach((step, i) => { step.order = i + 1 })
}

const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    const response = await useApi('/api/v1/approval-flows').post({ body: form.value })
    const saved = response?.data ?? response

    items.value.unshift(saved)
    dialog.value = false
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save approval flow error:', e)
  } finally {
    saving.value = false
  }
}

const headers = [
  { title: 'Kode', key: 'code', width: 160 },
  { title: 'Nama', key: 'name' },
  { title: 'Jenis Objek', key: 'approvable_type', width: 140 },
  { title: 'Jumlah Step', key: 'steps', width: 120 },
  { title: 'Status', key: 'is_active', align: 'center', width: 100 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Approval Flow
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Setup alur approval multi-level (§10.2/§24) yang dipakai modul lain (Document, CAPA, dst.)
            </p>
          </div>
          <VBtn
            v-if="ability.can('configure', 'Workflow')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Flow
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
      >
        <template #item.code="{ item }">
          <code>{{ item.code }}</code>
        </template>
        <template #item.approvable_type="{ item }">
          {{ item.approvable_type ?? '-' }}
        </template>
        <template #item.steps="{ item }">
          {{ item.steps?.length ?? 0 }}
        </template>
        <template #item.is_active="{ item }">
          <VChip
            size="small"
            :color="item.is_active ? 'success' : 'secondary'"
          >
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada flow approval
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="640"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          Tambah Approval Flow
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama Flow *"
                placeholder="mis. Approval Dokumen"
                :error-messages="errors.name"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="form.code"
                label="Kode *"
                placeholder="mis. approval-dokumen"
                :error-messages="errors.code"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="form.approvable_type"
                label="Jenis Objek (opsional)"
                placeholder="mis. document"
                hint="Alias morph map — cuma metadata, tidak divalidasi."
                persistent-hint
                :error-messages="errors.approvable_type"
              />
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_active"
                label="Aktif"
                color="success"
                hide-details
              />
            </VCol>

            <VCol cols="12">
              <div class="d-flex align-center justify-space-between mb-2">
                <p class="text-subtitle-2 mb-0">
                  Step Approval *
                </p>
                <VBtn
                  size="small"
                  variant="tonal"
                  prepend-icon="ri-add-line"
                  @click="addStep"
                >
                  Tambah Step
                </VBtn>
              </div>
              <div
                v-if="errors.steps"
                class="text-error text-caption mb-2"
              >
                {{ Array.isArray(errors.steps) ? errors.steps[0] : errors.steps }}
              </div>

              <VRow
                v-for="(step, index) in form.steps"
                :key="index"
                class="align-center"
              >
                <VCol cols="2">
                  <VTextField
                    v-model.number="step.order"
                    label="Urutan"
                    type="number"
                    density="compact"
                    :error-messages="errors[`steps.${index}.order`]"
                  />
                </VCol>
                <VCol cols="4">
                  <VTextField
                    v-model="step.name"
                    label="Nama Step"
                    placeholder="mis. Kepala Unit"
                    density="compact"
                    :error-messages="errors[`steps.${index}.name`]"
                  />
                </VCol>
                <VCol cols="5">
                  <VTextField
                    v-model="step.approver_role"
                    label="Role Approver"
                    placeholder="mis. kepala-unit"
                    density="compact"
                    :error-messages="errors[`steps.${index}.approver_role`]"
                  />
                </VCol>
                <VCol
                  cols="1"
                  class="d-flex justify-center"
                >
                  <VBtn
                    icon="ri-delete-bin-line"
                    variant="text"
                    color="error"
                    size="small"
                    :disabled="form.steps.length <= 1"
                    @click="removeStep(index)"
                  />
                </VCol>
              </VRow>
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
